import * as React from 'react';
import { useHistory } from 'react-router-dom';
import propz from 'propz';
import { Switch } from 'Src/components/Switch/Switch';
import { Loader } from 'Src/components/Loader/Loader';
import { AppUser } from 'Src/views/App/App';
import {
  getSchoolSetup,
  updateSchoolSetupSection,
  updateSchoolSetupStatus
} from 'Src/helpers/service/admin/schoolSetup';
import { updateSchoolAdvancedSettings, updateSchoolSettings } from 'Src/helpers/service/admin/settings';
import { getNotificationSettings, updateNotificationSettings } from 'Src/helpers/service/admin/notificationSettings';
import { NOTIFICATION_TYPE } from 'Src/consts/notifications';
import { SchoolSetupState } from 'Src/models/school';
import { uploadImage } from 'Src/helpers/service/image';
import { getPostcode, searchFunctionSchoolPostcodes } from 'Src/helpers/autocomplete/autocomplete';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import './SchoolSetup.scss';

interface Props {
  user: AppUser;
}
type StepKey =
  | 'SCHOOL_PROFILE'
  | 'NOTIFICATIONS'
  | 'AVAILABILITY'
  | 'EVENT_COMMUNICATIONS'
  | 'ACTIVITIES'
  | 'PUBLISHING'
  | 'REVIEW';

const MODULE_LABELS: { [key: string]: string } = {
  SPORT: 'Sport & events',
  CLUBS: 'Clubs & activities',
  COMMUNICATIONS: 'Communications',
  AWARDS: 'Awards'
};

const STEP_DEFINITIONS: Array<{ key: StepKey; title: string; description: string; modules?: string[] }> = [
  { key: 'SCHOOL_PROFILE', title: 'School profile', description: 'Identity, contact details and pupil name display.' },
  { key: 'NOTIFICATIONS', title: 'Notifications', description: 'Turn on the essential updates people need.' },
  {
    key: 'AVAILABILITY',
    title: 'Availability',
    description: 'How families and pupils confirm attendance.',
    modules: ['SPORT']
  },
  {
    key: 'EVENT_COMMUNICATIONS',
    title: 'Event chats',
    description: 'Access, posting, safeguarding and retention.',
    modules: ['COMMUNICATIONS', 'SPORT']
  },
  {
    key: 'ACTIVITIES',
    title: 'Events & activities',
    description: 'Set the defaults staff will use most often.',
    modules: ['SPORT', 'CLUBS']
  },
  {
    key: 'PUBLISHING',
    title: 'Website & big screen',
    description: 'Decide whether school information is visible outside the admin area.',
    modules: ['SPORT', 'CLUBS', 'AWARDS']
  },
  { key: 'REVIEW', title: 'Review & finish', description: 'Check the choices before opening your workspace.' }
];

const eventCommsDefaults = (school: any) => {
  const value: any = propz.get(school, ['settings', 'eventComms'], {});
  return {
    privateParentsPolicy: value.privateParentsPolicy || (value.privateParentsEnabled ? 'TWO_WAY' : 'DISABLED'),
    privateStudentsPolicy: value.privateStudentsPolicy || (value.privateStudentsEnabled ? 'TWO_WAY' : 'DISABLED'),
    publicChatParentsEnabled: Boolean(value.publicChatParentsEnabled),
    publicChatStudentsEnabled: Boolean(value.publicChatStudentsEnabled),
    publicChatParentsPosting: value.publicChatParentsPosting || 'IMMEDIATE',
    publicChatStudentsPosting: value.publicChatStudentsPosting || 'MODERATED',
    retentionYears: value.retentionYears || 7,
    postEventPublicReadOnlyHours:
      typeof value.postEventPublicReadOnlyHours === 'number' ? value.postEventPublicReadOnlyHours : 24,
    postEventPrivateResolveDays:
      typeof value.postEventPrivateResolveDays === 'number' ? value.postEventPrivateResolveDays : 7,
    askOnEventCancellation: value.askOnEventCancellation !== false
  };
};

const NOTIFICATION_CHOICES = [
  {
    key: NOTIFICATION_TYPE.PERMISSION_REQUEST_CREATED,
    title: 'New role requests',
    hint: 'Tell administrators when somebody asks to join the school.'
  },
  { key: NOTIFICATION_TYPE.EVENT_CREATED, title: 'New events', hint: 'Tell affected users when an event is created.' },
  {
    key: NOTIFICATION_TYPE.EVENT_UPDATED,
    title: 'Event changes',
    hint: 'Tell affected users when event details change.'
  },
  {
    key: NOTIFICATION_TYPE.EVENT_CANCELED,
    title: 'Event cancellations',
    hint: 'Tell affected users when an event is cancelled.'
  },
  {
    key: NOTIFICATION_TYPE.EVENT_AVAILABILITY_REQUESTED,
    title: 'Availability requests',
    hint: 'Tell parents or pupils when a response is required.',
    modules: ['SPORT']
  },
  {
    key: NOTIFICATION_TYPE.MESSAGE_INBOX_CONSENT_REQUEST,
    title: 'Consent requests',
    hint: 'Tell parents when the school requests consent.',
    modules: ['SPORT']
  },
  {
    key: NOTIFICATION_TYPE.CLUB_PARTICIPATION_INVITE,
    title: 'Activity invitations',
    hint: 'Tell families when a pupil is invited to an activity.',
    modules: ['CLUBS']
  },
  {
    key: NOTIFICATION_TYPE.AWARD_GRANTED,
    title: 'Awards granted',
    hint: 'Tell pupils and families when an award is granted.',
    modules: ['AWARDS']
  }
];

export const SchoolSetup: React.FC<Props> = ({ user }) => {
  const history = useHistory();
  const initialSchool: any = propz.get(user, ['activeSchool'], {});
  const [school, setSchool] = React.useState<any>(initialSchool);
  const [setup, setSetup] = React.useState<Partial<SchoolSetupState>>(initialSchool.setup || {});
  const [stepIndex, setStepIndex] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [uploadingLogo, setUploadingLogo] = React.useState(false);
  const [error, setError] = React.useState('');
  const [profile, setProfile] = React.useState({
    name: initialSchool.name || '',
    description: initialSchool.description || '',
    pic: initialSchool.pic || '',
    address: initialSchool.address || '',
    phone: initialSchool.phone || '',
    postcodeId: '',
    email: initialSchool.email || '',
    sportsDepartmentEmail: initialSchool.sportsDepartmentEmail || '',
    publicStudentViewType:
      initialSchool.publicStudentViewType === 'SHORTEN_FIRSTNAME_AND_LASTNAME'
        ? 'SHORTEN_FIRSTNAME_AND_LASTNAME'
        : 'SHORTEN_LASTNAME_ONLY',
    ageGroupsNaming: initialSchool.ageGroupsNaming || 'ENGLISH'
  });
  const [publishing, setPublishing] = React.useState({
    domain: initialSchool.domain || '',
    publicSiteStatus: propz.get(initialSchool, ['publicSite', 'status'], 'DISABLED'),
    publicSitePassword: '',
    publicBigscreenSiteStatus: propz.get(initialSchool, ['publicBigscreenSite', 'status'], 'DISABLED'),
    publicBigscreenSitePassword: ''
  });
  const [activityDefaults, setActivityDefaults] = React.useState({
    defaultFixtureStartTime: initialSchool.defaultFixtureStartTime || '10:00',
    defaultClubStartTime: initialSchool.defaultClubStartTime || '10:00',
    isEventUpdateNotificationCustomMessageEnabled: initialSchool.isEventUpdateNotificationCustomMessageEnabled !== false
  });
  const initialSettings: any = initialSchool.settings || {};
  const [availability, setAvailability] = React.useState({
    availabilityWorkflow: initialSettings.availabilityWorkflow || 'SELF_SERVICE',
    availabilityParentEnabled: initialSettings.availabilityParentEnabled === true,
    availabilityStudentEnabled: initialSettings.availabilityStudentEnabled === true,
    availabilityResponseEditingEnabled: initialSettings.availabilityResponseEditingEnabled !== false,
    availabilityAutoRequestRecipients: initialSettings.availabilityAutoRequestRecipients || 'BOTH',
    availabilityAutoDeadlineHoursBefore:
      initialSettings.availabilityAutoDeadlineHoursBefore === null
        ? null
        : initialSettings.availabilityAutoDeadlineHoursBefore || 24
  });
  const [eventComms, setEventComms] = React.useState(eventCommsDefaults(initialSchool));
  const [notifications, setNotifications] = React.useState<any>(initialSchool.notifications || {});
  const [notificationsLoaded, setNotificationsLoaded] = React.useState(false);

  React.useEffect(() => {
    getSchoolSetup(user)
      .then(value => {
        setSetup(value);
        if (value.status !== 'COMPLETED') {
          const activeModules =
            value.selectedTrialModules && value.selectedTrialModules.length > 0
              ? value.selectedTrialModules
              : (initialSchool.moduleSubscriptions || [])
                  .filter(item => ['ACTIVE', 'TRIAL'].includes(item.status))
                  .map(item => item.module);
          const availableSteps = STEP_DEFINITIONS.filter(
            step => !step.modules || step.modules.some(module => activeModules.includes(module))
          );
          const firstPendingIndex = availableSteps.findIndex(
            step =>
              step.key !== 'REVIEW' &&
              !['COMPLETED', 'SKIPPED'].includes(
                propz.get(
                  (value.sections || []).find(section => section.key === step.key),
                  ['status'],
                  'NOT_STARTED'
                )
              )
          );
          setStepIndex(firstPendingIndex === -1 ? Math.max(availableSteps.length - 1, 0) : firstPendingIndex);
        }
        if (value.status === 'NOT_STARTED') return updateSchoolSetupStatus(user, 'IN_PROGRESS').then(setSetup);
        return undefined;
      })
      .catch(() => setError('We could not load your setup progress. Please refresh and try again.'))
      .then(() => setLoading(false));
    getNotificationSettings(user)
      .then(value => {
        setNotifications(propz.get(value, ['notifications'], {}));
        setNotificationsLoaded(true);
      })
      .catch(() => setNotificationsLoaded(true));
  }, [user]);

  const subscribedModules = (initialSchool.moduleSubscriptions || [])
    .filter(item => ['ACTIVE', 'TRIAL'].includes(item.status))
    .map(item => item.module);
  const selectedModules =
    setup.selectedTrialModules && setup.selectedTrialModules.length > 0
      ? setup.selectedTrialModules
      : subscribedModules;
  const steps = STEP_DEFINITIONS.filter(
    step => !step.modules || step.modules.some(module => selectedModules.includes(module))
  );
  const current = steps[Math.min(stepIndex, steps.length - 1)];
  const savedSections: any[] = setup.sections || [];
  const sectionStatus = (key: string) =>
    propz.get(
      savedSections.find(item => item.key === key),
      ['status'],
      'NOT_STARTED'
    );
  const reviewedCount =
    steps.filter(step => step.key === 'REVIEW' || ['COMPLETED', 'SKIPPED'].includes(sectionStatus(step.key))).length -
    1;
  const progress = Math.round((reviewedCount / Math.max(steps.length - 1, 1)) * 100);

  const completeSection = async (key: StepKey) => {
    const next = await updateSchoolSetupSection(user, key, 'COMPLETED');
    setSetup(next);
  };
  const applySchoolUpdate = (updated: any) => {
    setSchool(currentSchool => ({ ...currentSchool, ...updated }));
    if (user.activeSchool) Object.assign(user.activeSchool, updated);
  };
  const saveCurrent = async () => {
    setSaving(true);
    setError('');
    try {
      if (current.key === 'SCHOOL_PROFILE') {
        if (!profile.name.trim()) throw new Error('Enter the school name before continuing.');
        const { postcodeId, ...profileValues } = profile;
        const updated: any = await updateSchoolSettings(user, {
          ...profileValues,
          ...(postcodeId ? { postcodeId } : {}),
          email: profile.email.trim() || null,
          sportsDepartmentEmail: profile.sportsDepartmentEmail.trim() || null
        });
        applySchoolUpdate(updated);
        await completeSection(current.key);
      }
      if (current.key === 'AVAILABILITY') {
        if (!availability.availabilityParentEnabled && !availability.availabilityStudentEnabled) {
          throw new Error('Enable parents or students so somebody can report availability.');
        }
        const allowed =
          availability.availabilityParentEnabled && availability.availabilityStudentEnabled
            ? ['PARENT', 'STUDENT', 'BOTH']
            : availability.availabilityParentEnabled
            ? ['PARENT']
            : ['STUDENT'];
        const normalized: any = { ...availability };
        if (!allowed.includes(normalized.availabilityAutoRequestRecipients)) {
          normalized.availabilityAutoRequestRecipients = allowed[0];
        }
        const settings: any = await updateSchoolAdvancedSettings(user, normalized);
        applySchoolUpdate({ settings: { ...school.settings, ...settings } });
        await completeSection(current.key);
      }
      if (current.key === 'NOTIFICATIONS') {
        const relevantChoices = NOTIFICATION_CHOICES.filter(
          choice => !choice.modules || choice.modules.some(module => selectedModules.includes(module))
        );
        const payload = relevantChoices.reduce(
          (result, choice) => ({ ...result, [choice.key]: notifications[choice.key] || 'DISABLED' }),
          {}
        );
        const updated: any = await updateNotificationSettings(user, payload);
        setNotifications(propz.get(updated, ['notifications'], notifications));
        await completeSection(current.key);
      }
      if (current.key === 'EVENT_COMMUNICATIONS') {
        const settings: any = await updateSchoolAdvancedSettings(user, { eventComms });
        applySchoolUpdate({ settings: { ...school.settings, ...settings } });
        await completeSection(current.key);
      }
      if (current.key === 'ACTIVITIES') {
        const updated: any = await updateSchoolSettings(user, activityDefaults);
        applySchoolUpdate(updated);
        await completeSection(current.key);
      }
      if (current.key === 'PUBLISHING') {
        if (
          (publishing.publicSiteStatus !== 'DISABLED' || publishing.publicBigscreenSiteStatus !== 'DISABLED') &&
          !publishing.domain.trim()
        ) {
          throw new Error('Choose the school web address before enabling the website or big screen.');
        }
        if (publishing.publicSiteStatus === 'PROTECTED' && !publishing.publicSitePassword.trim()) {
          throw new Error('Enter a password for the public school website.');
        }
        if (publishing.publicBigscreenSiteStatus === 'PROTECTED' && !publishing.publicBigscreenSitePassword.trim()) {
          throw new Error('Enter a password for the big screen.');
        }
        const updated: any = await updateSchoolSettings(user, {
          domain: publishing.domain.trim() || null,
          publicSite: {
            ...(school.publicSite || {}),
            status: publishing.publicSiteStatus,
            ...(publishing.publicSiteStatus === 'PROTECTED' ? { password: publishing.publicSitePassword.trim() } : {})
          },
          publicBigscreenSite: {
            ...(school.publicBigscreenSite || {}),
            status: publishing.publicBigscreenSiteStatus,
            ...(publishing.publicBigscreenSiteStatus === 'PROTECTED'
              ? { password: publishing.publicBigscreenSitePassword.trim() }
              : {})
          }
        });
        if (
          propz.get(updated, ['publicSite', 'status']) !== publishing.publicSiteStatus ||
          propz.get(updated, ['publicBigscreenSite', 'status']) !== publishing.publicBigscreenSiteStatus ||
          ((publishing.publicSiteStatus !== 'DISABLED' || publishing.publicBigscreenSiteStatus !== 'DISABLED') &&
            updated.domain !== publishing.domain.trim())
        ) {
          throw new Error('The website settings were not saved. Please check the web address and try again.');
        }
        applySchoolUpdate(updated);
        await completeSection(current.key);
      }
      setStepIndex(index => Math.min(index + 1, steps.length - 1));
    } catch (reason) {
      setError(reason && reason.message ? reason.message : 'These settings could not be saved. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  const skipCurrent = async () => {
    setSaving(true);
    setError('');
    try {
      const next = await updateSchoolSetupSection(user, current.key, 'SKIPPED');
      setSetup(next);
      setStepIndex(index => Math.min(index + 1, steps.length - 1));
    } catch (reason) {
      setError('This section could not be skipped. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  const finish = async () => {
    setSaving(true);
    setError('');
    try {
      await updateSchoolSetupStatus(user, 'COMPLETED');
      history.push('/dashboard');
    } catch (reason) {
      setError('Setup could not be completed. Please try again.');
      setSaving(false);
    }
  };
  const setAvailabilityValue = (key: string, value: any) =>
    setAvailability(currentValue => ({ ...currentValue, [key]: value }));
  const setCommsValue = (key: string, value: any) => setEventComms(currentValue => ({ ...currentValue, [key]: value }));
  const setPublishingValue = (key: string, value: any) =>
    setPublishing(currentValue => ({ ...currentValue, [key]: value }));
  const onLogoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'].includes(file.type)) {
      setError('Choose a JPG, PNG, WebP or SVG image for the school logo.');
      return;
    }
    setUploadingLogo(true);
    setError('');
    try {
      const response: any = await uploadImage(user, file);
      const pic = response && response.key ? `${window.apiImg}/images/${response.key}` : '';
      if (!pic) throw new Error();
      setProfile(value => ({ ...value, pic }));
    } catch (reason) {
      setError('The school logo could not be uploaded. Please try again.');
    } finally {
      setUploadingLogo(false);
    }
  };
  const ToggleRow = ({ title, hint, value, onChange }) => (
    <div className="schoolSetup__toggleRow">
      <div>
        <strong>{title}</strong>
        <span>{hint}</span>
      </div>
      <Switch name={title} text="" value={value} onChange={onChange} />
    </div>
  );

  if (loading) {
    return (
      <div className="schoolSetup schoolSetup--loading">
        <Loader />
      </div>
    );
  }
  const chatsAvailable = school.eventCommunicationsAvailable !== false;
  const isReviewMode = setup.status === 'COMPLETED';
  return (
    <div className="schoolSetup schoolSetup--wizard">
      <header className="schoolSetup__wizardHeader">
        <div>
          <span className="schoolSetup__eyebrow">{isReviewMode ? 'School settings review' : 'School setup'}</span>
          <h1>
            {isReviewMode ? `Review ${school.name || 'your school'}` : `Let’s set up ${school.name || 'your school'}`}
          </h1>
          <p>
            {isReviewMode
              ? 'Work through the sections to check that the school still operates the way you expect.'
              : 'Make the essential choices here. You can review every setting again later.'}
          </p>
        </div>
        <div className="schoolSetup__progress">
          <strong>{progress}%</strong>
          <span>
            {reviewedCount} of {steps.length - 1} sections reviewed
          </span>
          <div>
            <i style={{ width: `${progress}%` }} />
          </div>
        </div>
      </header>
      {selectedModules.length > 0 && (
        <div className="schoolSetup__moduleStrip">
          <strong>Your trial</strong>
          {selectedModules.map(module => (
            <span key={module}>{MODULE_LABELS[module] || module}</span>
          ))}
        </div>
      )}
      <div className="schoolSetup__workspace">
        <nav className="schoolSetup__rail" aria-label="Setup steps">
          {steps.map((step, index) => {
            const status = sectionStatus(step.key);
            return (
              <button
                type="button"
                key={step.key}
                className={`${index === stepIndex ? 'is-active' : ''} is-${String(status).toLowerCase()}`}
                onClick={() => setStepIndex(index)}
              >
                <i>{index + 1}</i>
                <span>
                  <strong>{step.title}</strong>
                  <small>{step.description}</small>
                </span>
                <b>{status === 'COMPLETED' ? '✓' : status === 'SKIPPED' ? 'Skipped' : ''}</b>
              </button>
            );
          })}
        </nav>
        <main className="schoolSetup__panel">
          <div className="schoolSetup__panelHeader">
            <span>
              Step {stepIndex + 1} of {steps.length}
            </span>
            <h2>{current.title}</h2>
            <p>{current.description}</p>
          </div>
          {error && (
            <div className="schoolSetup__error" role="alert">
              {error}
            </div>
          )}
          {current.key === 'SCHOOL_PROFILE' && (
            <div className="schoolSetup__formGrid">
              <div className="schoolSetup__logo is-wide">
                <div className="schoolSetup__logoPreview">
                  {uploadingLogo ? (
                    <Loader />
                  ) : profile.pic ? (
                    <img src={profile.pic} alt="School logo" />
                  ) : (
                    <span>{(profile.name || 'S').charAt(0)}</span>
                  )}
                </div>
                <div>
                  <strong>School logo</strong>
                  <p>Used throughout the platform, public website and big screen.</p>
                  <label className="schoolSetup__logoButton">
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" onChange={onLogoChange} />
                    {uploadingLogo ? 'Uploading…' : profile.pic ? 'Replace logo' : 'Upload logo'}
                  </label>
                </div>
              </div>
              <label className="is-wide">
                <span>School name *</span>
                <input value={profile.name} onChange={event => setProfile({ ...profile, name: event.target.value })} />
              </label>
              <label className="is-wide">
                <span>Description</span>
                <textarea
                  rows={4}
                  value={profile.description}
                  onChange={event => setProfile({ ...profile, description: event.target.value })}
                />
                <small>Introduce the school on public-facing pages and shared school information.</small>
              </label>
              <label className="is-wide">
                <span>Address</span>
                <textarea
                  rows={3}
                  value={profile.address}
                  onChange={event => setProfile({ ...profile, address: event.target.value })}
                />
              </label>
              <label>
                <span>Postcode</span>
                <Autocomplete
                  defaultItem={initialSchool.postcode}
                  searchFunction={value => searchFunctionSchoolPostcodes(user, value)}
                  getElementTitle={getPostcode}
                  onSelect={item => setProfile({ ...profile, postcodeId: item.id || item._id })}
                  placeholder="Start typing a postcode"
                />
              </label>
              <label>
                <span>Main email</span>
                <input
                  type="email"
                  value={profile.email}
                  onChange={event => setProfile({ ...profile, email: event.target.value })}
                />
              </label>
              <label>
                <span>Sports department email</span>
                <input
                  type="email"
                  value={profile.sportsDepartmentEmail}
                  onChange={event => setProfile({ ...profile, sportsDepartmentEmail: event.target.value })}
                />
              </label>
              <label>
                <span>Phone</span>
                <input
                  value={profile.phone}
                  onChange={event => setProfile({ ...profile, phone: event.target.value })}
                />
              </label>
              <label>
                <span>Age group naming</span>
                <select
                  value={profile.ageGroupsNaming}
                  onChange={event => setProfile({ ...profile, ageGroupsNaming: event.target.value })}
                >
                  <option value="ENGLISH">English school years</option>
                  <option value="SCOTTISH">Scottish school years</option>
                  <option value="U5-U18">Under 5–Under 18</option>
                  <option value="US">US grades</option>
                </select>
              </label>
              <label className="is-wide">
                <span>How pupil names are displayed</span>
                <select
                  value={profile.publicStudentViewType}
                  onChange={event => setProfile({ ...profile, publicStudentViewType: event.target.value })}
                >
                  <option value="SHORTEN_LASTNAME_ONLY">First name and last initial</option>
                  <option value="SHORTEN_FIRSTNAME_AND_LASTNAME">Initials only</option>
                </select>
                <small>
                  This controls how pupil names appear inside parent and student accounts, to other schools on event
                  screens, and on your public website and big screen. Full names are never exposed through this setting.
                </small>
              </label>
            </div>
          )}
          {current.key === 'NOTIFICATIONS' && (
            <div className="schoolSetup__sectionStack">
              <div className="schoolSetup__guidance">
                <strong>Only the essentials</strong>
                <p>
                  This short list covers the most important automated updates for your selected modules. Detailed
                  notification rules and reminder timings remain available for later review.
                </p>
              </div>
              {!notificationsLoaded ? (
                <div className="schoolSetup__inlineLoader">
                  <Loader />
                </div>
              ) : (
                NOTIFICATION_CHOICES.filter(
                  choice => !choice.modules || choice.modules.some(module => selectedModules.includes(module))
                ).map(choice => (
                  <ToggleRow
                    key={choice.key}
                    title={choice.title}
                    hint={choice.hint}
                    value={notifications[choice.key] === 'AUTO'}
                    onChange={() =>
                      setNotifications({
                        ...notifications,
                        [choice.key]: notifications[choice.key] === 'AUTO' ? 'DISABLED' : 'AUTO'
                      })
                    }
                  />
                ))
              )}
            </div>
          )}
          {current.key === 'AVAILABILITY' && (
            <div className="schoolSetup__sectionStack">
              <div className="schoolSetup__choiceGrid">
                {[
                  ['SELF_SERVICE', 'Self-service', 'Families can report availability without waiting for a request.'],
                  ['MANUAL_REQUEST', 'Staff request it', 'Staff choose when and whom to ask from each event.'],
                  [
                    'AUTO_ON_PLAYER_ADDED',
                    'Automatic requests',
                    'Create a request whenever a pupil is added to an event.'
                  ]
                ].map(item => (
                  <button
                    type="button"
                    key={item[0]}
                    className={availability.availabilityWorkflow === item[0] ? 'is-selected' : ''}
                    onClick={() => setAvailabilityValue('availabilityWorkflow', item[0])}
                  >
                    <strong>{item[1]}</strong>
                    <span>{item[2]}</span>
                  </button>
                ))}
              </div>
              <ToggleRow
                title="Parents can respond"
                hint="Parents report for children linked to their account."
                value={availability.availabilityParentEnabled}
                onChange={() =>
                  setAvailabilityValue('availabilityParentEnabled', !availability.availabilityParentEnabled)
                }
              />
              <ToggleRow
                title="Students can respond"
                hint="Students report using their own account."
                value={availability.availabilityStudentEnabled}
                onChange={() =>
                  setAvailabilityValue('availabilityStudentEnabled', !availability.availabilityStudentEnabled)
                }
              />
              <ToggleRow
                title="Allow response changes"
                hint="Every change is retained in the response history."
                value={availability.availabilityResponseEditingEnabled}
                onChange={() =>
                  setAvailabilityValue(
                    'availabilityResponseEditingEnabled',
                    !availability.availabilityResponseEditingEnabled
                  )
                }
              />
              {availability.availabilityWorkflow === 'AUTO_ON_PLAYER_ADDED' && (
                <label className="schoolSetup__selectRow">
                  <span>
                    <strong>Response deadline</strong>
                    <small>How long before the event responses close.</small>
                  </span>
                  <select
                    value={
                      availability.availabilityAutoDeadlineHoursBefore === null
                        ? 'NONE'
                        : availability.availabilityAutoDeadlineHoursBefore
                    }
                    onChange={event =>
                      setAvailabilityValue(
                        'availabilityAutoDeadlineHoursBefore',
                        event.target.value === 'NONE' ? null : Number(event.target.value)
                      )
                    }
                  >
                    <option value="NONE">Until the event starts</option>
                    <option value={24}>24 hours before</option>
                    <option value={48}>48 hours before</option>
                    <option value={72}>3 days before</option>
                    <option value={168}>7 days before</option>
                  </select>
                </label>
              )}
            </div>
          )}
          {current.key === 'EVENT_COMMUNICATIONS' &&
            (!chatsAvailable ? (
              <div className="schoolSetup__locked">
                <strong>Event chats are not enabled for this school</strong>
                <p>
                  You can continue setup now. Contact Squad In Touch when you are ready to add secure event
                  conversations.
                </p>
              </div>
            ) : (
              <div className="schoolSetup__sectionStack">
                <ToggleRow
                  title="Parents can view shared event chat"
                  hint="Only parents of participating pupils receive access."
                  value={eventComms.publicChatParentsEnabled}
                  onChange={() => setCommsValue('publicChatParentsEnabled', !eventComms.publicChatParentsEnabled)}
                />
                <ToggleRow
                  title="Students can view shared event chat"
                  hint="Only participating students receive access."
                  value={eventComms.publicChatStudentsEnabled}
                  onChange={() => setCommsValue('publicChatStudentsEnabled', !eventComms.publicChatStudentsEnabled)}
                />
                <label className="schoolSetup__selectRow">
                  <span>
                    <strong>Private chats with parents</strong>
                    <small>Authorised staff retain safeguarding access.</small>
                  </span>
                  <select
                    value={eventComms.privateParentsPolicy}
                    onChange={event => setCommsValue('privateParentsPolicy', event.target.value)}
                  >
                    <option value="DISABLED">Disabled</option>
                    <option value="STAFF_INITIATED">Staff can initiate</option>
                    <option value="TWO_WAY">Staff or parents can initiate</option>
                  </select>
                </label>
                <label className="schoolSetup__selectRow">
                  <span>
                    <strong>Private chats with students</strong>
                    <small>Authorised staff retain safeguarding access.</small>
                  </span>
                  <select
                    value={eventComms.privateStudentsPolicy}
                    onChange={event => setCommsValue('privateStudentsPolicy', event.target.value)}
                  >
                    <option value="DISABLED">Disabled</option>
                    <option value="STAFF_INITIATED">Staff can initiate</option>
                    <option value="TWO_WAY">Staff or students can initiate</option>
                  </select>
                </label>
                <label className="schoolSetup__selectRow">
                  <span>
                    <strong>Safeguarding retention</strong>
                    <small>Seven years is recommended.</small>
                  </span>
                  <select
                    value={eventComms.retentionYears}
                    onChange={event => setCommsValue('retentionYears', Number(event.target.value))}
                  >
                    <option value={3}>3 years</option>
                    <option value={5}>5 years</option>
                    <option value={7}>7 years</option>
                  </select>
                </label>
              </div>
            ))}
          {current.key === 'ACTIVITIES' && (
            <div className="schoolSetup__sectionStack">
              <div className="schoolSetup__guidance">
                <strong>Save time for every organiser</strong>
                <p>
                  These defaults prefill new records. Staff can still choose a different time or age naming when the
                  activity needs it.
                </p>
              </div>
              {selectedModules.includes('SPORT') && (
                <label className="schoolSetup__selectRow">
                  <span>
                    <strong>Default fixture start time</strong>
                    <small>Prefilled when staff create a new fixture.</small>
                  </span>
                  <input
                    className="schoolSetup__timeInput"
                    type="time"
                    step={300}
                    value={activityDefaults.defaultFixtureStartTime}
                    onChange={event =>
                      setActivityDefaults({ ...activityDefaults, defaultFixtureStartTime: event.target.value })
                    }
                  />
                </label>
              )}
              {selectedModules.includes('CLUBS') && (
                <label className="schoolSetup__selectRow">
                  <span>
                    <strong>Default activity start time</strong>
                    <small>Prefilled when staff create a new club or activity.</small>
                  </span>
                  <input
                    className="schoolSetup__timeInput"
                    type="time"
                    step={300}
                    value={activityDefaults.defaultClubStartTime}
                    onChange={event =>
                      setActivityDefaults({ ...activityDefaults, defaultClubStartTime: event.target.value })
                    }
                  />
                </label>
              )}
              {selectedModules.includes('SPORT') && (
                <ToggleRow
                  title="Let staff add a message when event details change"
                  hint="Staff can explain what changed before notifications are sent."
                  value={activityDefaults.isEventUpdateNotificationCustomMessageEnabled}
                  onChange={() =>
                    setActivityDefaults({
                      ...activityDefaults,
                      isEventUpdateNotificationCustomMessageEnabled: !activityDefaults.isEventUpdateNotificationCustomMessageEnabled
                    })
                  }
                />
              )}
            </div>
          )}
          {current.key === 'PUBLISHING' && (
            <div className="schoolSetup__sectionStack">
              <div className="schoolSetup__guidance">
                <strong>Keep everything private while you prepare</strong>
                <p>
                  Disabled is the safest starting point. You can publish either surface later without changing the other
                  one.
                </p>
              </div>
              <label className="schoolSetup__selectRow">
                <span>
                  <strong>Public school website</strong>
                  <small>Fixtures, results, news and other school content.</small>
                </span>
                <select
                  value={publishing.publicSiteStatus}
                  onChange={event => setPublishingValue('publicSiteStatus', event.target.value)}
                >
                  <option value="DISABLED">Disabled</option>
                  <option value="PROTECTED">Password protected</option>
                  <option value="PUBLIC_AVAILABLE">Public</option>
                </select>
              </label>
              {publishing.publicSiteStatus === 'PROTECTED' && (
                <label className="schoolSetup__domain">
                  <span>Website password *</span>
                  <input
                    type="password"
                    value={publishing.publicSitePassword}
                    onChange={event => setPublishingValue('publicSitePassword', event.target.value)}
                    autoComplete="new-password"
                  />
                </label>
              )}
              <label className="schoolSetup__selectRow">
                <span>
                  <strong>Big screen</strong>
                  <small>A rotating display for reception, sports halls or shared screens.</small>
                </span>
                <select
                  value={publishing.publicBigscreenSiteStatus}
                  onChange={event => setPublishingValue('publicBigscreenSiteStatus', event.target.value)}
                >
                  <option value="DISABLED">Disabled</option>
                  <option value="PROTECTED">Password protected</option>
                  <option value="PUBLIC_AVAILABLE">Public</option>
                </select>
              </label>
              {publishing.publicBigscreenSiteStatus === 'PROTECTED' && (
                <label className="schoolSetup__domain">
                  <span>Big screen password *</span>
                  <input
                    type="password"
                    value={publishing.publicBigscreenSitePassword}
                    onChange={event => setPublishingValue('publicBigscreenSitePassword', event.target.value)}
                    autoComplete="new-password"
                  />
                </label>
              )}
              {(publishing.publicSiteStatus !== 'DISABLED' || publishing.publicBigscreenSiteStatus !== 'DISABLED') && (
                <label className="schoolSetup__domain">
                  <span>School web address *</span>
                  <div>
                    <input
                      value={publishing.domain}
                      onChange={event =>
                        setPublishingValue('domain', event.target.value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase())
                      }
                      placeholder="your-school"
                    />
                    <b>.squadintouch.com</b>
                  </div>
                  <small>Use letters, numbers and hyphens only.</small>
                </label>
              )}
            </div>
          )}
          {current.key === 'REVIEW' && (
            <div className="schoolSetup__review">
              <div>
                <strong>School profile</strong>
                <span>{school.name || profile.name}</span>
                <small>{sectionStatus('SCHOOL_PROFILE').toLowerCase()}</small>
              </div>
              <div>
                <strong>Notifications</strong>
                <span>
                  {Object.keys(notifications).filter(key => notifications[key] === 'AUTO').length} automated updates
                  enabled
                </span>
                <small>{sectionStatus('NOTIFICATIONS').toLowerCase()}</small>
              </div>
              {steps.some(step => step.key === 'AVAILABILITY') && (
                <div>
                  <strong>Availability</strong>
                  <span>{availability.availabilityWorkflow.replace(/_/g, ' ').toLowerCase()}</span>
                  <small>{sectionStatus('AVAILABILITY').toLowerCase()}</small>
                </div>
              )}
              {steps.some(step => step.key === 'EVENT_COMMUNICATIONS') && (
                <div>
                  <strong>Event chats</strong>
                  <span>{chatsAvailable ? 'Configured for your audiences' : 'Not enabled'}</span>
                  <small>{sectionStatus('EVENT_COMMUNICATIONS').toLowerCase()}</small>
                </div>
              )}
              {steps.some(step => step.key === 'ACTIVITIES') && (
                <div>
                  <strong>Events & activities</strong>
                  <span>Default times and event updates</span>
                  <small>{sectionStatus('ACTIVITIES').toLowerCase()}</small>
                </div>
              )}
              {steps.some(step => step.key === 'PUBLISHING') && (
                <div>
                  <strong>Website & big screen</strong>
                  <span>
                    {publishing.publicSiteStatus === 'DISABLED' && publishing.publicBigscreenSiteStatus === 'DISABLED'
                      ? 'Private for now'
                      : 'At least one surface enabled'}
                  </span>
                  <small>{sectionStatus('PUBLISHING').toLowerCase()}</small>
                </div>
              )}
              <p>
                Completing setup does not lock anything. Administrators can return and review these choices whenever the
                school changes.
              </p>
            </div>
          )}
          <footer className="schoolSetup__footer">
            <button
              type="button"
              className="is-secondary"
              disabled={stepIndex === 0 || saving}
              onClick={() => setStepIndex(index => Math.max(0, index - 1))}
            >
              Back
            </button>
            <span />
            {current.key !== 'REVIEW' && (
              <button type="button" className="is-link" disabled={saving} onClick={skipCurrent}>
                Skip for now
              </button>
            )}
            <button
              type="button"
              className="is-primary"
              disabled={saving || uploadingLogo || (current.key === 'NOTIFICATIONS' && !notificationsLoaded)}
              onClick={current.key === 'REVIEW' ? finish : saveCurrent}
            >
              {saving
                ? 'Saving…'
                : current.key === 'REVIEW'
                ? isReviewMode
                  ? 'Finish review'
                  : 'Finish setup'
                : 'Save & continue'}
            </button>
          </footer>
        </main>
      </div>
    </div>
  );
};

SchoolSetup.displayName = 'SchoolSetup';
